import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, MailX } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { api, errorMessage } from "@/lib/api";

const Shell = ({ children, testid }) => (
  <div className="container-site section">
    <div className="card-soft mx-auto max-w-lg p-8 text-center sm:p-10" data-testid={testid}>
      {children}
    </div>
  </div>
);

export function SubscriptionConfirm() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ status: token ? "loading" : "error", message: token ? "" : "This confirmation link is missing its token." });
  const ran = useRef(false);
  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    api
      .post("/subscribers/confirm", { token })
      .then((r) => setState({ status: "ok", message: r.data.message }))
      .catch((e) => setState({ status: "error", message: errorMessage(e) }));
  }, [token]);
  return (
    <>
      <Seo title="Confirm subscription | Uhlumo Organics" description="Confirm your Uhlumo Organics Community subscription." path="/subscription/confirm" noindex />
      <Shell testid="subscription-confirm">
        {state.status === "loading" ? <Loader2 className="mx-auto h-8 w-8 animate-spin text-[color:var(--u-green)]" aria-hidden="true" /> : state.status === "ok" ? <CheckCircle2 className="mx-auto h-10 w-10 text-[color:var(--u-green)]" aria-hidden="true" /> : <XCircle className="mx-auto h-10 w-10 text-[#B42318]" aria-hidden="true" />}
        <h1 className="mt-4 text-3xl font-semibold">{state.status === "loading" ? "Confirming…" : state.status === "ok" ? "You’re in!" : "Link not valid"}</h1>
        <p className="mt-3 text-muted-foreground" role="status">
          {state.message}
        </p>
        <Link to="/products" className="btn btn-primary mt-7">
          Explore our products
        </Link>
      </Shell>
    </>
  );
}

export function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ status: token ? "idle" : "error", message: token ? "" : "This unsubscribe link is missing its token. Email thozama@uhlumoorganics.co.za and we will remove you." });
  const submit = async () => {
    setState({ status: "loading" });
    try {
      const r = await api.post("/subscribers/unsubscribe", { token });
      setState({ status: "ok", message: r.data.message });
    } catch (e) {
      setState({ status: "error", message: errorMessage(e) });
    }
  };
  return (
    <>
      <Seo title="Unsubscribe | Uhlumo Organics" description="Unsubscribe from Uhlumo Organics marketing emails." path="/unsubscribe" noindex />
      <Shell testid="unsubscribe-page">
        <MailX className="mx-auto h-10 w-10 text-[color:var(--u-brown)]" aria-hidden="true" />
        <h1 className="mt-4 text-3xl font-semibold">{state.status === "ok" ? "You’ve been unsubscribed" : "Unsubscribe from marketing emails"}</h1>
        {state.status === "idle" || state.status === "loading" ? (
          <>
            <p className="mt-3 text-muted-foreground">You will no longer receive product news or promotional emails from Uhlumo Organics.</p>
            <button className="btn btn-primary mt-7" onClick={submit} disabled={state.status === "loading"} data-testid="unsubscribe-confirm-button">
              {state.status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Confirm unsubscribe
            </button>
          </>
        ) : (
          <p className="mt-3 text-muted-foreground" role="status" data-testid="unsubscribe-message">
            {state.message}
          </p>
        )}
        <div className="mt-6">
          <Link to="/" className="link-accent text-sm">
            Return to the homepage
          </Link>
        </div>
      </Shell>
    </>
  );
}

export function NotFound() {
  return (
    <>
      <Seo title="Page not found | Uhlumo Organics" description="The page you are looking for could not be found." path="/404" noindex />
      <Shell testid="not-found-page">
        <p className="eyebrow justify-center">404</p>
        <h1 className="mt-3 text-3xl font-semibold">We couldn’t find that page</h1>
        <p className="mt-3 text-muted-foreground">It may have moved. Try our products or get in touch.</p>
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Link to="/products" className="btn btn-primary">
            Explore Products
          </Link>
          <Link to="/contact" className="btn btn-outline">
            Contact Us
          </Link>
        </div>
      </Shell>
    </>
  );
}
